/**
 * Custom error classes for translation operations
 * Used for type-safe exception handling in repositories and handlers
 */

/**
 * Base error class for all translation-related errors
 */
export class TranslationError extends Error {
  constructor(
    public readonly key: string,
    public readonly status: number,
    message: string
  ) {
    super(message);
    this.name = this.constructor.name;
    // Maintains proper stack trace for where our error was thrown
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, this.constructor);
    }
  }
}

/**
 * Translation not found in backend (HTTP 404)
 * Should be synced to backend for translators to fill in
 */
export class TranslationNotFoundError extends TranslationError {
  constructor(key: string) {
    super(key, 404, `Translation not found: ${key}`);
  }
}

/**
 * Translation exists but is locked/not ready (HTTP 423)
 * Should NOT be synced to backend - will retry on next app start
 */
export class TranslationLockedError extends TranslationError {
  constructor(
    key: string,
    public readonly retryAfter?: number
  ) {
    super(key, 423, `Translation locked: ${key}`);
  }
}

/**
 * Server error (HTTP 5xx)
 * Should trigger retry logic with exponential backoff
 */
export class ServerError extends TranslationError {
  constructor(key: string, status: number) {
    super(key, status, `Server error ${status}: ${key}`);
  }
}

/**
 * Network error (timeout, DNS failure, CORS, etc.)
 * Not an HTTP error - network layer failed
 */
export class NetworkError extends Error {
  constructor(message: string, public readonly cause?: unknown) {
    super(message);
    this.name = 'NetworkError';
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, this.constructor);
    }
  }
}

/**
 * Type guard to check if error is a TranslationError
 */
export function isTranslationError(error: unknown): error is TranslationError {
  return error instanceof TranslationError;
}

/**
 * Type guard to check if error is retryable (5xx or network)
 */
export function isRetryableError(error: unknown): boolean {
  return error instanceof ServerError || error instanceof NetworkError;
}

/**
 * Type guard to check if error is client error (4xx - not retryable)
 */
export function isClientError(error: unknown): boolean {
  return (
    error instanceof TranslationNotFoundError ||
    error instanceof TranslationLockedError
  );
}
